import React, { useEffect, useCallback } from 'react'
import { X } from 'lucide-react'
import { cn } from '../../lib/cn'
import { useOverlayLayer } from './overlayStack'
import { useDialogFocus } from './useDialogFocus'

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | 'full'
export type ModalLayer = 'default' | 'nested'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  subtitle?: string
  size?: ModalSize
  children: React.ReactNode
  footer?: React.ReactNode
  closeOnOverlayClick?: boolean
  closeOnEsc?: boolean
  showCloseButton?: boolean
  layer?: ModalLayer
  className?: string
}

const sizeStyles: Record<ModalSize, string> = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  /** Рабочее окно в две колонки: шире xl, но не во весь экран. */
  xxl: 'max-w-6xl',
  full: 'max-w-[95vw] max-h-[95vh]',
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  size = 'md',
  children,
  footer,
  closeOnOverlayClick = true,
  closeOnEsc = true,
  showCloseButton = true,
  layer = 'default',
  className,
}) => {
  const titleId = React.useId()
  const descriptionId = React.useId()
  const dialogRef = React.useRef<HTMLDivElement>(null)
  // Блокировкой прокрутки теперь заведует стек: иначе закрытие вложенного окна
  // разблокировало бы страницу под ещё открытым родителем.
  const { isTopmost } = useOverlayLayer(isOpen)
  useDialogFocus(isOpen, dialogRef, isTopmost)

  const handleEsc = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      // Клавишей владеет верхний оверлей. Не верхний — молчит и событие не
      // гасит: гасить должен тот, кто его обработал.
      if (!isTopmost()) return
      // Esc уже обработал элемент внутри окна (открытый список Select/Combobox закрылся и погасил событие
      // через preventDefault) — окно не закрываем: одно нажатие закрывает один слой.
      if (event.defaultPrevented) return
      if (closeOnEsc) onClose()
    },
    [closeOnEsc, onClose, isTopmost],
  )

  useEffect(() => {
    if (!isOpen) return
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [isOpen, handleEsc])

  if (!isOpen) return null

  return (
    <>
      <div
        className={cn(
          'fixed inset-0 !m-0 bg-black/40 animate-fadeIn',
          layer === 'nested' ? 'z-[80]' : 'z-40',
        )}
        onClick={closeOnOverlayClick ? onClose : undefined}
        aria-hidden="true"
      />

      <div
        className={cn(
          'fixed inset-0 !m-0 flex items-center justify-center p-4 pointer-events-none',
          layer === 'nested' ? 'z-[90]' : 'z-50',
        )}
      >
        <div
          ref={dialogRef}
          className={cn(
            'pointer-events-auto bg-white dark:bg-zinc-900 rounded-lg shadow-medium w-full',
            'flex flex-col max-h-[90vh] overflow-hidden',
            sizeStyles[size],
            className,
          )}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          data-pim-dialog=""
          tabIndex={-1}
          aria-modal="true"
          aria-labelledby={title ? titleId : undefined}
          aria-label={title ? undefined : 'Диалог'}
          aria-describedby={subtitle ? descriptionId : undefined}
        >
          {(title || subtitle || showCloseButton) && (
            <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-gray-200 dark:border-zinc-800 shrink-0">
              <div className="min-w-0 flex-1">
                {title && (
                  <h3
                    id={titleId}
                    className="text-lg font-semibold text-gray-900 dark:text-zinc-100 leading-tight"
                  >
                    {title}
                  </h3>
                )}
                {subtitle && (
                  <p id={descriptionId} className="mt-0.5 text-xs text-gray-500 dark:text-zinc-400">{subtitle}</p>
                )}
              </div>
              {showCloseButton && (
                <button
                  type="button"
                  onClick={onClose}
                  className="shrink-0 -mt-1 -mr-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:text-zinc-200 dark:hover:bg-zinc-800 transition-colors p-1.5 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                  aria-label="Закрыть"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          )}

          <div className="flex-1 overflow-y-auto break-words px-6 py-5 text-gray-900 dark:text-zinc-100">
            {children}
          </div>

          {footer && (
            <div className="flex shrink-0 flex-col-reverse items-stretch justify-end gap-2 border-t border-gray-200 bg-gray-50 px-6 py-3 [&>button]:w-full sm:flex-row sm:items-center sm:[&>button]:w-auto dark:border-zinc-800 dark:bg-zinc-900/60">
              {footer}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default Modal
